import React, { useState, useEffect, useCallback } from "react";
import styled from "styled-components";
import {
  FiArrowLeft,
  FiDollarSign,
  FiLogOut,
  FiShield,
  FiUser,
} from "react-icons/fi";
import { COLORS } from "./utils/constants";
import { adminApi, transactionsApi } from "./api";
import { AuthProvider } from "./hooks/useAuth";
import Dashboard from "./components/Dashboard";
import ExpenseForm from "./components/ExpenseForm";
import TransactionList from "./components/TransactionList";
import AdminPanel from "./components/AdminPanel";
import FundingSummary from "./components/FundingSummary";
import LoginPage from "./components/LoginPage";

const PageWrapper = styled.div`
  background: ${COLORS.gray50};
  min-height: 100vh;
`;

const TopBar = styled.header`
  background: ${COLORS.white};
  border-bottom: 1px solid ${COLORS.gray200};
  padding: 16px 24px;

  @media (max-width: 600px) {
    padding: 12px 16px;
  }
`;

const TopBarInner = styled.div`
  align-items: center;
  display: flex;
  gap: 12px;
  justify-content: space-between;
  margin: 0 auto;
  max-width: 1100px;
`;

const TopBarLeft = styled.div`
  align-items: center;
  display: flex;
  gap: 12px;
`;

const TopBarRight = styled.div`
  align-items: center;
  display: flex;
  gap: 16px;
`;

const LogoIcon = styled.div`
  align-items: center;
  background: ${COLORS.primary};
  border-radius: 8px;
  color: ${COLORS.white};
  display: flex;
  height: 36px;
  justify-content: center;
  width: 36px;
`;

const AppTitle = styled.h1`
  color: ${COLORS.gray800};
  font-size: 20px;
  font-weight: 600;
  margin: 0;

  @media (max-width: 480px) {
    font-size: 16px;
  }
`;

const UserDisplay = styled.div`
  align-items: center;
  color: ${COLORS.gray600};
  display: flex;
  font-size: 14px;
  gap: 6px;

  @media (max-width: 480px) {
    display: none;
  }
`;

const RoleBadge = styled.span`
  background: ${(props) =>
    props.$role === "admin" ? COLORS.warningLight : COLORS.primaryLight};
  border-radius: 10px;
  color: ${(props) =>
    props.$role === "admin" ? COLORS.warning : COLORS.primary};
  font-size: 11px;
  font-weight: 600;
  padding: 2px 8px;
  text-transform: uppercase;
`;

const LogoutButton = styled.button`
  align-items: center;
  background: none;
  border: 1px solid ${COLORS.gray300};
  border-radius: 6px;
  color: ${COLORS.gray600};
  cursor: pointer;
  display: flex;
  font-size: 13px;
  gap: 6px;
  padding: 6px 12px;

  &:hover {
    background: ${COLORS.gray50};
    border-color: ${COLORS.gray400};
  }

  @media (max-width: 480px) {
    padding: 6px 8px;
  }
`;

const TabBar = styled.div`
  border-bottom: 1px solid ${COLORS.gray200};
  display: flex;
  gap: 0;
  margin: 0 auto;
  max-width: 1100px;
  padding: 0 24px;

  @media (max-width: 600px) {
    padding: 0 16px;
  }
`;

const Tab = styled.button`
  align-items: center;
  background: none;
  border: none;
  border-bottom: 2px solid
    ${(props) => (props.$active ? COLORS.primary : "transparent")};
  color: ${(props) => (props.$active ? COLORS.primary : COLORS.gray500)};
  cursor: pointer;
  display: flex;
  flex: 1;
  font-size: 14px;
  font-weight: 500;
  gap: 6px;
  justify-content: center;
  padding: 12px 20px;

  &:hover {
    color: ${COLORS.primary};
  }

  @media (min-width: 601px) {
    flex: none;
  }
`;

const Main = styled.main`
  display: flex;
  flex-direction: column;
  gap: 24px;
  margin: 0 auto;
  max-width: 1100px;
  padding: 24px;

  @media (max-width: 600px) {
    gap: 16px;
    padding: 16px;
  }
`;

const TwoColumn = styled.div`
  display: grid;
  gap: 24px;
  grid-template-columns: 380px 1fr;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const LoadingPage = styled.div`
  align-items: center;
  background: ${COLORS.gray50};
  color: ${COLORS.gray500};
  display: flex;
  font-size: 16px;
  justify-content: center;
  min-height: 100vh;
`;

const ImpersonationBanner = styled.div`
  align-items: center;
  background: ${COLORS.warning};
  color: ${COLORS.white};
  display: flex;
  font-size: 13px;
  font-weight: 500;
  gap: 12px;
  justify-content: center;
  padding: 8px 16px;
`;

const ReturnButton = styled.button`
  align-items: center;
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.4);
  border-radius: 6px;
  color: ${COLORS.white};
  cursor: pointer;
  display: flex;
  font-size: 12px;
  font-weight: 500;
  gap: 4px;
  padding: 4px 12px;

  &:hover {
    background: rgba(255, 255, 255, 0.3);
  }
`;

const emptySummary = {
  byCurrency: [],
  transactionCount: 0,
};

const MainApp = (props) => {
  const { user, logout, refreshUser } = props;
  const [tab, setTab] = useState("my");
  const [transactions, setTransactions] = useState([]);
  const [summary, setSummary] = useState(emptySummary);

  const isImpersonating = !!user.impersonating;
  const isAdmin = user.role === "admin" && !isImpersonating;

  const loadData = useCallback(async () => {
    try {
      const [txData, sumData] = await Promise.all([
        transactionsApi.list(),
        transactionsApi.summary(),
      ]);
      setTransactions(txData);
      setSummary(sumData);
    } catch (err) {
      console.error("Failed to load data:", err);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Reload data when user changes (e.g. after impersonation)
  useEffect(() => {
    loadData();
  }, [user.id, loadData]);

  const handleAddTransaction = async (formData) => {
    try {
      await transactionsApi.create(formData);
      loadData();
    } catch (err) {
      alert("Failed to save transaction: " + err.message);
    }
  };

  const handleDeleteTransaction = async (id) => {
    try {
      await transactionsApi.delete(id);
      loadData();
    } catch (err) {
      alert("Failed to delete transaction: " + err.message);
    }
  };

  const handleImpersonate = async (userId) => {
    try {
      await adminApi.impersonate(userId);
      await refreshUser();
      setTab("my");
    } catch (err) {
      alert("Failed to switch user: " + err.message);
    }
  };

  const handleStopImpersonating = async () => {
    try {
      await adminApi.stopImpersonating();
      await refreshUser();
      setTab("admin");
    } catch (err) {
      alert("Failed to return to admin: " + err.message);
    }
  };

  return (
    <PageWrapper>
      {isImpersonating && (
        <ImpersonationBanner>
          Viewing as {user.display_name}
          <ReturnButton onClick={handleStopImpersonating}>
            <FiArrowLeft size={12} /> Return to Admin
          </ReturnButton>
        </ImpersonationBanner>
      )}

      <TopBar>
        <TopBarInner>
          <TopBarLeft>
            <LogoIcon>
              <FiDollarSign size={20} />
            </LogoIcon>
            <AppTitle>Petty Cash Tracker</AppTitle>
          </TopBarLeft>
          <TopBarRight>
            <UserDisplay>
              {user.display_name}
              <RoleBadge $role={user.role}>{user.role}</RoleBadge>
            </UserDisplay>
            {isImpersonating ? (
              <LogoutButton onClick={handleStopImpersonating}>
                <FiArrowLeft size={14} /> Return
              </LogoutButton>
            ) : (
              <LogoutButton onClick={logout}>
                <FiLogOut size={14} /> Sign out
              </LogoutButton>
            )}
          </TopBarRight>
        </TopBarInner>
      </TopBar>

      {isAdmin && (
        <TabBar>
          <Tab $active={tab === "my"} onClick={() => setTab("my")}>
            <FiUser size={14} /> My Expenses
          </Tab>
          <Tab $active={tab === "admin"} onClick={() => setTab("admin")}>
            <FiShield size={14} /> Admin Panel
          </Tab>
        </TabBar>
      )}

      <Main>
        {tab === "my" || isImpersonating ? (
          <React.Fragment>
            <Dashboard summary={summary} />
            <FundingSummary />
            <TwoColumn>
              <ExpenseForm
                defaultCurrency={user.default_currency}
                onAddTransaction={handleAddTransaction}
              />
              <TransactionList
                transactions={transactions}
                onDeleteTransaction={handleDeleteTransaction}
              />
            </TwoColumn>
          </React.Fragment>
        ) : (
          <AdminPanel currentUser={user} onImpersonate={handleImpersonate} />
        )}
      </Main>
    </PageWrapper>
  );
};

const App = () => {
  return (
    <AuthProvider>
      {(auth) => {
        if (auth.loading) {
          return <LoadingPage>Loading...</LoadingPage>;
        }

        if (!auth.user) {
          return <LoginPage onLogin={auth.login} onRegister={auth.register} />;
        }

        return (
          <MainApp
            user={auth.user}
            logout={auth.logout}
            refreshUser={auth.refreshUser}
          />
        );
      }}
    </AuthProvider>
  );
};

export default App;
